/** Small, code-native dock icons. The class catalog owns the names and rules;
 * the ability kind owns the symbol, so a store rename cannot change its read. */
import type { AbilityKind } from "@pd/shared";

export type DockIcon = AbilityKind | "summon-pgt" | "teleport-pgt" | "teleport-ggs" | "dungeon-map" | "empty";

const SHAPES: Record<DockIcon, string> = {
  "next-hit-bonus": '<path d="M8 25 23 10m-4-3 6 6M6 27l5-2-4-4-1 6Z"/><path d="m22 4 1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3Z"/>',
  "next-hit-reduce": '<path d="M16 4 27 8v7c0 7-4 10-11 13C9 25 5 22 5 15V8l11-4Z"/><path d="m11 17 3 3 7-8"/>',
  "next-hit-splash": '<path d="M7 25 21 11m-4-4 8 8M5 27l5-2-3-3-2 5Z"/><path d="M25 5v3m0 4v3m-7-7h3m8 0h-2M23 21l3 3m-8 1-1 3M29 20l-2 1"/>',
  "damage-immune": '<path d="M16 3 27 8v8c0 6-4 10-11 13C9 26 5 22 5 16V8L16 3Z"/><path d="M11 17h10m-5-5v10"/>',
  "damage-reflect": '<path d="M16 3 27 8v8c0 6-4 10-11 13C9 26 5 22 5 16V8L16 3Z"/><path d="m10 19 6-6 6 6m-6-6v11"/>',
  "pull-vortex": '<path d="M16 16c-1-3 2-5 5-4 4 1 4 7 0 10-5 4-13 1-14-5C6 9 15 4 23 8"/><path d="m21 5 3 3-4 2"/><circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none"/>',
  "next-hit-chain": '<path d="m18 3-9 14h6l-2 12 10-15h-6l1-11Z"/><path d="M5 9 3 7m24 18 2 2M26 5l2-2"/>',
  "room-aoe": '<circle cx="16" cy="16" r="4"/><circle cx="16" cy="16" r="10" stroke-dasharray="3 3"/><path d="M16 2v5m0 18v5M2 16h5m18 0h5"/>',
  "speed-buff": '<path d="M7 23c5 1 7-1 9-7l3-8 4 2-2 9 5 3v4H7v-3Z"/><path d="M3 11h8M2 16h7M5 7h6"/>',
  teleport: '<path d="M6 26h20V11L16 5 6 11v15Z"/><path d="M12 26v-9a4 4 0 0 1 8 0v9M16 13v9m-3-3 3 3 3-3"/>',
  // A rising firebird: swept feathered wings, a crested head and a flame
  // tail. Keep the silhouette inside the stroke margin at small dock sizes.
  "phoenix-teleport": '<path d="M14 13c-4-1-8-5-11-9 0 6 2 11 8 14l-5-1c2 3 5 4 8 3M19 13c4-1 7-5 10-9 0 6-2 11-8 14l5-1c-2 3-5 4-8 3"/><path d="M13 10c0-3 2-5 5-5l-1 3c2 0 4 1 4 3l-3 1v6c0 3-1 5-3 7l-2 3c-2-3-2-6 0-9"/><path d="m7 11 5 4m13-4-5 4M17 23c1 2 1 4-1 6"/><circle cx="17" cy="10" r="0.8" fill="currentColor" stroke="none"/>',
  telegrab: '<path d="M6 24h10m-4-4 4 4-4 4"/><path d="M19 26c5 0 8-3 8-7v-5c0-2-3-2-3 0v-4c0-2-3-2-3 0V8c0-2-3-2-3 0v5c-2-2-5-1-5 1l6 12Z"/><circle cx="7" cy="9" r="3"/>',
  "summon-pgt": '<path d="m16 10 8 5-2 10H10L8 15l8-5Z"/><path d="M16 3v5m-6-2 2 3m10-3-2 3M16 15v6m-3-3h6"/>',
  "teleport-pgt": '<path d="m16 5 9 6-2 13H9L7 11l9-6Z"/><path d="M16 10v10m-4-4 4 4 4-4"/><circle cx="16" cy="24" r="1" fill="currentColor" stroke="none"/>',
  "teleport-ggs": '<path d="m16 4 11 8-3 13H8L5 12 16 4Z"/><path d="M16 9v11m-4-4 4 4 4-4M9 25h14"/>',
  "dungeon-map": '<path d="m4 8 8-3 8 3 8-3v19l-8 3-8-3-8 3V8Z"/><path d="M12 5v19m8-16v19"/><path d="m7 18 3-3 3 2 4-5 4 3 4-4" stroke-dasharray="2 2"/><circle cx="25" cy="11" r="1.5" fill="currentColor" stroke="none"/>',
  empty: '<circle cx="16" cy="16" r="10" stroke-dasharray="2 4"/>',
};

/** The buff bar's symbols. Three are new (the consumable buff families have
 *  no ability of their own to borrow a glyph from); three reuse an ability
 *  glyph, because Barricade/Reflect/Sprint ARE those abilities and the bar
 *  must read as the same object the dock does. */
export type BuffIcon =
  | "buff-damage"
  | "buff-damage-taken"
  | "buff-accuracy"
  | "buff-freeze"
  | "buff-speed"
  | "buff-immune"
  | "buff-reflect";

const BUFF_SHAPES: Record<BuffIcon, string> = {
  // A sword, point up: outgoing damage.
  "buff-damage": '<path d="M16 3l3 4v11l-3 3-3-3V7l3-4Z"/><path d="M10 21h12M16 21v8m-3 0h6"/>',
  // A shield with a crack: damage you take.
  "buff-damage-taken": '<path d="M16 3 27 8v8c0 6-4 10-11 13C9 26 5 22 5 16V8L16 3Z"/><path d="m16 7-3 6 4 2-3 8"/>',
  // A crosshair: accuracy.
  "buff-accuracy": '<circle cx="16" cy="16" r="8"/><circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none"/><path d="M16 3v5m0 21v-5M3 16h5m21 0h-5"/>',
  // Double chevrons: haste. The Sprint dock glyph is a boot, which turns to
  // mud at the bar's 22 px — the bar needs a silhouette, not a drawing.
  "buff-freeze": '<path d="M16 4v24M6 10l20 12M6 22l20-12M12 7l4 4 4-4M12 25l4-4 4 4M7 14l6-1-2-6M25 18l-6 1 2 6M7 18l6 1-2 6M25 14l-6-1 2-6"/>',
  "buff-speed": '<path d="m9 7 7 9-7 9M18 7l7 9-7 9"/>',
  "buff-immune": SHAPES["damage-immune"],
  // Reflect borrows Barricade's shield, so it has to differ INSIDE it, and it
  // must not read as Speed: chevrons pointing BACK at the attacker (Speed's
  // point forward).
  "buff-reflect": '<path d="M16 3 27 8v8c0 6-4 10-11 13C9 26 5 22 5 16V8L16 3Z"/><path d="m15 11-4 5 4 5M21 11l-4 5 4 5"/>',
};

export function buffIconSvg(icon: BuffIcon): string {
  return svg(BUFF_SHAPES[icon]);
}

function svg(inner: string): string {
  return `<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
}

export function dockIconSvg(icon: DockIcon): string {
  return svg(SHAPES[icon]);
}
